import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { jsonParse } from 'utils/json';
import { getHasStoredPropertyValue } from 'utils/properties';

import { selectMasterCompanyId } from 'store/auth/selectors';

import { getCompany } from './company.selectors';

const toPropertyValueMap = properties => {
  if (!properties) {
    return {};
  }

  const propertyList = Array.isArray(properties) ? properties : Object.values(properties);

  return propertyList.reduce((acc, item) => {
    if (item && item.propertyName) {
      acc[item.propertyName] = item.propertyValue;
    }

    return acc;
  }, {});
};

export const getCompanyProperties = createSelector(getCompany, ({ properties }) => properties);

const selectCompanyProperties = memoizeOne(companyId =>
  createSelector(getCompany, ({ properties }) => properties[companyId]),
);

const selectCompanyPropertiesMap = memoizeOne(companyId =>
  createSelector(selectCompanyProperties(companyId), companyProperties =>
    _.keyBy(companyProperties, 'propertyName'),
  ),
);

export const selectCompanyPropertyByPropertyName = memoizeOne((companyId, propertyName) =>
  createSelector(
    selectCompanyPropertiesMap(companyId),
    companyPropertiesMap => companyPropertiesMap[propertyName],
  ),
);

export const selectCompanyPropertiesAsObject = memoizeOne(companyId =>
  createSelector(selectCompanyProperties(companyId), toPropertyValueMap),
);

export const selectConfiguredCompanyPropertyNames = memoizeOne(companyId =>
  createSelector(
    selectCompanyProperties(companyId),
    companyProperties =>
      new Set(
        _.filter(companyProperties, getHasStoredPropertyValue).map(
          ({ propertyName }) => propertyName,
        ),
      ),
  ),
);

const selectMasterCompanyProperties = createSelector(
  [getCompany, selectMasterCompanyId],
  ({ properties }, masterCompanyId) => properties[masterCompanyId],
);

const selectMasterCompanyPropertiesMap = createSelector(
  selectMasterCompanyProperties,
  companyProperties => _.keyBy(companyProperties, 'propertyName'),
);

export const selectMasterCompanyPropertiesAsObject = createSelector(
  selectMasterCompanyProperties,
  toPropertyValueMap,
);

export const selectFannieLendersOptionsByPropertyName = memoizeOne(propertyName =>
  createSelector(selectMasterCompanyPropertiesMap, companyPropertiesMap => {
    const fannieLenders = companyPropertiesMap[propertyName];

    if (!fannieLenders) {
      return [];
    }

    const { propertyValue } = fannieLenders;
    const propertyValueMap = jsonParse(propertyValue);

    const fannieLendersOptions = Object.entries(propertyValueMap).map(([value, name]) => ({
      value: Number(value),
      name,
    }));

    return fannieLendersOptions;
  }),
);
